import { useCallback, useEffect, useState } from 'react';

import { useContainerContext } from '@nextroll/ar-container-context';
import { ComponentPropertyInputConfig } from 'types/propertyGeneration';
import { singlePropertyValueLookup } from 'api/workflowActionsApi';
import { SingleLookup, SingleLookupUpdateWorkflowParams } from 'components/basic/inputProperty/SingleLookup/SingleLookup';
import { INodeBasePanelElementConfig } from 'components/basic/NodeBase/NodeBase';
import { RequestError, UseRequest } from 'hooks/useRequest';
import { InputProperty, WorkflowAction } from 'types/roflWorkflowActions';
import { IPropertyError } from 'types/rofl';
import { generateInitialMetadata } from 'store/nodeSlice';
import { generatePropertyErrorKey } from 'util/errors';
import { LookupType } from 'types/roflWorkflowActionProperty';


/**
 * GenerateSingleLookupConfig are the params for the useGenerateSingleLookupConfig hook
 */
export interface GenerateSingleLookupConfig {
    workflowAction: WorkflowAction | undefined;
    propertyErrors: Record<string, IPropertyError> | undefined;
    authedWithoutMissingScopes: boolean; // is this node authed correctly without any missing scopes
    call: UseRequest[0];
    handleRequestError: (requestError: RequestError) => boolean;
    fieldInitialPropertyTextGenerated: Record<string, boolean>;
    setFieldInitialPropertyTextGenerated: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

/**
 * useGenerateSingleLookupConfig returns a function to generate the config for the single lookup property input
 */
function useGenerateSingleLookupConfig({
    workflowAction,
    propertyErrors,
    authedWithoutMissingScopes,
    call,
    handleRequestError,
    fieldInitialPropertyTextGenerated,
    setFieldInitialPropertyTextGenerated,
}: GenerateSingleLookupConfig) {
    const { advertisable: { eid: advertisableEid = '' } = {} } = useContainerContext();

    // singleLookupIdentifierValid holds the association of whether or not a field (by identifier is valid or not)
    const [singleLookupIdentifierValid, setSingleLookupIdentifierValid] = useState<Record<string, boolean>>({});

    // cleanup
    useEffect(() => {
        setSingleLookupIdentifierValid({});
    }, [workflowAction]);

    /**
     *  generateSingleLookup dynamically generates single text lookups from the passed in config
     */
    const generateSingleLookupConfig = useCallback(
        (inputConfig: ComponentPropertyInputConfig): INodeBasePanelElementConfig | undefined => {
            const fieldKey = `workload-service-node-panel-element-${inputConfig.inputProperty.name}`;
            if (!workflowAction || inputConfig.inputProperty.lookup?.type !== LookupType.SINGLE) {
                return;
            }

            const propertyError = propertyErrors?.[generatePropertyErrorKey(inputConfig.inputProperty.name)];
            const getData = async (config: {
                lookupText: string;
                abortSignal?: AbortSignal;
                onError?: (requestError: RequestError) => boolean;
            }) => singlePropertyValueLookup({
                lookupText: config.lookupText,
                abortSignal: config.abortSignal,
                onError: (requestError: RequestError) => (
                    // handle global ActionNode errors first, and if there is nothing
                    // handle any specific to the field
                    handleRequestError(requestError) || !!config.onError?.(requestError)
                ),
                advertisableEid,
                workflowActionEid: workflowAction.eid,
                call,
                propertyName: inputConfig.inputProperty.name,
            });
            return {
                key: fieldKey,
                component: () => SingleLookup(
                    {
                        workflowActionEid: workflowAction?.eid,
                        identifier: fieldKey,
                        inputProperty: inputConfig.inputProperty,
                        setIsValid: setSingleLookupIdentifierValid,
                        // this function updates the node value and property metadata from this field
                        updateWorkflow: (
                            { value, actionMetadataProperty } : SingleLookupUpdateWorkflowParams
                        ) => {
                            // we don't need to lookup the value for the propertyText if we retrieved it
                            setFieldInitialPropertyTextGenerated(
                                (currentFieldInitialPropertyTextGenerated: Record<string, boolean>) => {
                                    return { ...currentFieldInitialPropertyTextGenerated, [fieldKey]: true };
                                }
                            );
                            const metadata = inputConfig.actionMetadata || generateInitialMetadata();
                            inputConfig.handleUpdateWorkflowNode({
                                ...inputConfig.workflowConfig, [inputConfig.inputProperty.name]: value
                            }, {
                                ...metadata,
                                properties: {
                                    ...metadata.properties,
                                    [inputConfig.inputProperty.name]: actionMetadataProperty
                                }
                            });
                        },
                        getData,
                        actionMetadataProperty: inputConfig.actionMetadataProperty,
                        propertyError
                    },
                ),
                isRequired: inputConfig.inputProperty.required,
                validate: () => singleLookupIdentifierValid[fieldKey],
                getTextForNode: async workflowActionConfig => {
                    let parentNodeText: string | undefined;
                    // on first entry we need to re-grab the node text from the api,
                    // otherwise we use what we have stored
                    if (fieldInitialPropertyTextGenerated[fieldKey]) {
                        parentNodeText = workflowActionConfig.metadata?.properties[inputConfig.inputProperty.name]
                            .lookup?.single_lookup?.selected.display_value;
                    } else {
                        setFieldInitialPropertyTextGenerated(
                            (currentFieldInitialPropertyTextGenerated: Record<string, boolean>) => {
                                return { ...currentFieldInitialPropertyTextGenerated, [fieldKey]: true };
                            }
                        );
                        const lookupText = workflowActionConfig.metadata?.properties[inputConfig.inputProperty.name]
                            .lookup?.single_lookup?.text;

                        // if this field has an error don't look it up
                        if (lookupText && authedWithoutMissingScopes && !propertyError) {
                            const result = await getData({ lookupText });
                            if (result) {
                                parentNodeText = result.display_value;
                            }
                        }
                    }
                    return parentNodeText || '';
                }
            };
        }, [advertisableEid,
            call,
            fieldInitialPropertyTextGenerated,
            handleRequestError,
            propertyErrors,
            authedWithoutMissingScopes,
            setFieldInitialPropertyTextGenerated,
            singleLookupIdentifierValid,
            workflowAction
        ]);

    /**
     *  isSingleLookupConfig returns whether or not the inputProperty will be handled by this generator
     */
    const isSingleLookupConfig = useCallback((inputProperty: InputProperty): boolean => {
        return inputProperty.lookup?.type === LookupType.SINGLE;
    }, []);

    return { generateSingleLookupConfig, isSingleLookupConfig };
}

export default useGenerateSingleLookupConfig;
